import IconButton from '@mui/material/IconButton';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import { JSONSchema7 } from 'json-schema';
import Tooltip from '@mui/material/Tooltip';
import { MouseEvent, useContext } from 'react';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind,
  LastUpdatedInfo
} from '../../WorkflowEngine/workflowEngineReducer';
import ControlState from '../../Shared/controlState';
import { put } from '../../Shared/ajax';
import PageState from '../../Shared/pageState';
import { useSnackbar } from 'notistack';

interface UpdatePageRequest {
  externalId: string;
  metadata: string;
}

interface UpdatePageResponse {
  externalId: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  metadata: string;
}

export default ({ control }: { control: ControlState }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { state, dispatch } = useContext(WorkflowEngineContext);

  const { pages } = state;

  const ctrlProperties = control.schema?.properties as JSONSchema7Properties;

  const isConditionalEnabled =
    Object.keys(ctrlProperties).includes('conditional') &&
    ((ctrlProperties['conditional'] as JSONSchema7)?.default as boolean);

  const saveControlChangesSync = async (pageState: PageState) => {
    try {
      const response = await put<UpdatePageResponse, UpdatePageRequest>(
        'DynamicWorkflowPage',
        {
          externalId: pageState.id,
          metadata: JSON.stringify(pageState.schema)
        }
      );

      dispatch({
        type: WorkflowEngineActionKind.SET_LAST_UPDATED_INFO,
        payload: {
          externalId: response.externalId,
          lastUpdatedDate: response.lastUpdatedDate,
          lastUpdatedBy: response.lastUpdatedBy,
          metadata: response.metadata
        } as LastUpdatedInfo
      });

      enqueueSnackbar('Control was successfully updated!', {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar((err as Error)?.message, { variant: 'error' });
    }
  };

  const toggleControlConditional = (ctrl: ControlState): ControlState => {
    const properties = ctrl.schema?.properties as JSONSchema7Properties;
    properties['conditional'] = {
      $id: `#${ctrl.pageId}/${ctrl.id}/conditional`,
      default: !(properties['conditional']?.default as boolean),
      type: 'boolean'
    };
    return {
      ...ctrl,
      properties
    } as ControlState;
  };

  const handleClickAsync = async (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();

    const ctrlPage = pages.find(
      (page) => page.id === control.pageId
    ) as PageState;

    dispatch({
      type: WorkflowEngineActionKind.CONTROL_UPDATE,
      payload: toggleControlConditional(control)
    });

    if (!control.building) {
      await saveControlChangesSync(ctrlPage);
    }
  };

  const isDisabled = (ctrl: ControlState) => {
    const properties = ctrl.schema?.properties as JSONSchema7Properties;
    const isControlStatusSelection =
      Object.keys(properties).includes('statusEnabled') &&
      (properties['statusEnabled']?.default as boolean);

    return isControlStatusSelection;
  };

  const getBtnTitle = () => {
    return `${isConditionalEnabled ? 'Show' : 'Hide'}${
      isDisabled(control) ? ' (Disabled when status selection is enabled)' : ''
    }`;
  };

  return (
    <Tooltip title={getBtnTitle()} followCursor>
      <span>
        <IconButton
          aria-label={getBtnTitle()}
          onClick={handleClickAsync}
          disabled={isDisabled(control)}
          size="small"
        >
          {isConditionalEnabled ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
